import {
  BufferGeometry,
  Euler,
  Group,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  Quaternion,
  Scene,
  Vector3,
} from 'three';

import { Cuboid } from '../annos';
import { ISubView, SubViews } from './SubViews';

export type ThreeViewType = 'top' | 'side' | 'front';

export type ViewData = {
  type: ThreeViewType;
  width: number;
  height: number;
  zoom: number;
  dir?: number;
  fitPosition: [number, number, number][];
  position: [number, number, number];
};

export interface ThreeViewOptions {
  type: ThreeViewType;

  // 三视图排列方向，涉及到宽高计算，【目前仅支持垂直排列】
  dir?: 'vertical';
  // 表示三视图到右侧边缘的距离，【绝对数值】，例如：10 -> 距离右侧边缘10px
  right?: number;
  // 表示三视图到上边缘的距离，【绝对数值】
  top?: number;
  // 表示三视图到下边缘的距离，【绝对数值】
  bottom?: number;

  // 大于1时为【绝对数值】，表示单个视图的宽度；小于1时为【相对数值】，表示相对于总视图宽度的百分比
  width: number;
  // 大于1时为【绝对数值】，表示单个视图的高度；小于1时为【相对数值】，表示相对于总视图高度的百分比
  height: number;

  // zoom含义：zoom = 视觉渲染范围 / 矩形长边
  zoom: number;
}
export interface ThreeView extends ISubView {
  // 对应视图类型
  name: ThreeViewType;

  cameraContainer: Group;
  originOptions: ThreeViewOptions;
}
export interface ViewCameraOptions {
  eye: [number, number, number];
  up: [number, number, number];
  target: [number, number, number];
  near: number;
  far: number;
}
const viewCameraOptions: Record<ThreeViewType, ViewCameraOptions> = {
  // 顶视图
  top: {
    eye: [0, 0, 0],
    up: [0, 1, 0],
    target: [0, 0, -3],
    near: -3,
    far: 3,
  },
  // 侧视图 - y轴
  side: {
    eye: [0, 0, 0],
    up: [0, 0, 1],
    target: [0, 3, 0],
    near: -3,
    far: 3,
  },
  // 前视图 - x轴
  front: {
    eye: [0, 0, 0],
    up: [0, 0, 1],
    target: [-3, 0, 0],
    near: -3,
    far: 3,
  },
};

const getAbsoluteValue = (val: number, total: number) => {
  if (val <= 0) {
    return 0;
  } else if (val <= 1) {
    return val * total;
  } else {
    return val;
  }
};

const _position = new Vector3();

// 注：直接 fill(new Vector3()) 会指向同一个 Vector3，应避免。
const _points = new Array(6).fill(0).map(() => new Vector3());
let _pointUpdate = false;

// 对应 selectCubeCritical 的平面顺序，0-5 依次代表左平面、右平面、上平面、下平面、近平面、远平面。
const fitPointMap: Record<ThreeViewType, number[]> = {
  top: [0, 1, 2, 3],
  front: [2, 3, 4, 5],
  side: [0, 1, 4, 5],
};

export class ThreeViews extends SubViews {
  public views: ThreeView[];

  private target?: Mesh<BufferGeometry, MeshBasicMaterial>;

  constructor(
    stageWidth: number,
    stageHeight: number,
    scene: Scene,
    viewOptions: ThreeViewOptions[],
    isShow = false,
    visibleLayer = 1
  ) {
    super([], isShow, visibleLayer);

    let totalHeight = 0;
    this.views = viewOptions.map((opt) => {
      const { type, zoom, height, width, top = 0, bottom = 0, right = 0 } = opt;
      const renderHeight = stageHeight - top - bottom,
        renderWidth = stageWidth - right;

      const realWidth = getAbsoluteValue(width, renderWidth);
      const realHeight = getAbsoluteValue(height, renderHeight);
      const realLeft = stageWidth - right - realWidth;
      const realBottom = stageHeight - top - totalHeight - realHeight;

      totalHeight += realHeight;

      const { camera, cameraContainer } = this.createCamera(realWidth, realHeight, type, zoom);
      scene.add(cameraContainer);

      return {
        name: type,
        zoom,
        camera,
        cameraContainer,
        viewport: [realLeft, realBottom, realWidth, realHeight],
        originOptions: opt,
      };
    });
  }

  public updateBoxScaleFromView(
    cube: Mesh<BufferGeometry, MeshBasicMaterial>,
    viewType: ThreeViewType,
    width: number,
    height: number,
    direction: { x: number; y: number },
    zoom: number
  ) {
    const { scale, rotation } = cube;

    const boxWidth = width / zoom;
    const boxHeight = height / zoom;
    const { x, y } = direction;

    let dir = new Vector3();

    // 调整尺寸
    switch (viewType) {
      case 'top':
        dir.set((x * (boxWidth - scale.x)) / 2, (-y * (boxHeight - scale.y)) / 2, 0);
        cube.scale.set(boxWidth, boxHeight, scale.z);
        break;
      case 'side':
        dir.set((x * (boxWidth - scale.x)) / 2, 0, (-y * (boxHeight - scale.z)) / 2);
        cube.scale.set(boxWidth, scale.y, boxHeight);
        break;
      case 'front':
        dir.set(0, (x * (boxWidth - scale.y)) / 2, (-y * (boxHeight - scale.z)) / 2);
        cube.scale.set(scale.x, boxWidth, boxHeight);
        break;
      default:
        break;
    }

    // 调整中心点
    const ruler = new Euler(rotation.x, rotation.y, rotation.z, 'XYZ');
    dir.applyEuler(ruler);
    cube.position.add(dir);
  }

  public updateBoxScaleFromCritical(cube: Mesh<BufferGeometry, MeshBasicMaterial>, criticalPoint: number[]) {
    if (criticalPoint.length < 18) return;
    for (let i = 0; i < criticalPoint.length; i = i + 3) {
      _points[i / 3].set(criticalPoint[i], criticalPoint[i + 1], criticalPoint[i + 2]);
    }
    const { position, scale } = Cuboid.getMinCubeFromPoints(_points, cube.quaternion);

    cube.position.copy(position);
    cube.scale.copy(scale);

    _pointUpdate = true;
  }

  public updateBoxQuaternionFromView(
    cube: Mesh<BufferGeometry, MeshBasicMaterial>,
    viewType: ThreeViewType,
    theta: number
  ) {
    const axis = {
      top: [0, 0, 1],
      side: [0, 1, 0],
      front: [1, 0, 0],
    };
    const vector = new Vector3(...axis[viewType]);
    cube.quaternion
      .multiply(new Quaternion().setFromAxisAngle(vector, viewType === 'side' ? theta : -theta))
      .normalize();
  }

  public updateBoxDirectionFromView(
    cube: Mesh<BufferGeometry, MeshBasicMaterial>,
    viewType: ThreeViewType,
    times: number
  ) {
    const theta = -(Math.PI / 2) * times;
    this.updateBoxQuaternionFromView(cube, viewType, theta);
  }

  public updateBoxPositionFromView(
    cube: Mesh<BufferGeometry, MeshBasicMaterial>,
    viewType: ThreeViewType,
    moveDelta: [number, number]
  ) {
    const [x, y] = moveDelta;
    const positionDelta: [number, number, number] = [0, 0, 0];
    switch (viewType) {
      case 'top':
        positionDelta[0] = x;
        positionDelta[1] = y;
        break;
      case 'side':
        positionDelta[0] = x;
        positionDelta[2] = y;
        break;
      case 'front':
        positionDelta[1] = x;
        positionDelta[2] = y;
        break;
    }

    _position.fromArray(positionDelta).applyQuaternion(cube.quaternion);
    cube.position.add(_position);
  }

  public focusOnBox(box: Mesh<BufferGeometry, MeshBasicMaterial>) {
    if (box !== this.target) {
      // 保证只能选中的立体框才能在视图中出现
      box.traverse((obj) => obj.layers.enable(this.layer));
      this.target?.traverse((obj) => obj.layers.disable(this.layer));
      this.target = box;
    }
    const rectData: ViewData[] = [];
    const boxDirection = box.userData.direction;

    this.views.forEach(({ name: type, cameraContainer, zoom, camera }, i) => {
      this.updateCameraPose(box, cameraContainer);
      const size = this.updateCameraRange(box, i);
      const dir = boxDirection ? this.getBoxDirInView(boxDirection, type) : undefined;
      const fitPosition = _pointUpdate
        ? _points.filter((_, i) => fitPointMap[type].includes(i)).map((v3) => v3.toArray())
        : [];
      rectData.push({ ...size, zoom, dir, fitPosition, position: box.position.toArray() });
    });
    _pointUpdate = false;
    return rectData;
  }

  public cameraFollowTarget() {
    const box = this.target;
    if (!box) return;
    this.views.forEach(({ cameraContainer }, i) => {
      this.updateCameraPose(box, cameraContainer);
    });
  }

  public updateCameraSize(stageWidth: number, stageHeight: number) {
    let totalHeight = 0;
    this.views.forEach(({ originOptions }, i) => {
      const { width: optWidth, height: optHeight, top = 0, bottom = 0, right = 0 } = originOptions;
      const renderHeight = stageHeight - top - bottom,
        renderWidth = stageWidth - right;

      const realWidth = getAbsoluteValue(optWidth, renderWidth);
      const realHeight = getAbsoluteValue(optHeight, renderHeight);

      this.views[i].viewport[0] = stageWidth - right - realWidth;
      this.views[i].viewport[1] = stageHeight - top - totalHeight - realHeight;
      this.views[i].viewport[2] = realWidth;
      this.views[i].viewport[3] = realHeight;

      totalHeight += realHeight;
    });
  }

  /**
   * 根据立方体的位置和尺寸，更新相机 Container 的位置和旋转
   * @param box
   * @param cameraContainer
   */
  private updateCameraPose(box: Mesh<BufferGeometry, MeshBasicMaterial>, cameraContainer: Group) {
    const { position, rotation } = box;
    cameraContainer.position.copy(position);
    cameraContainer.rotation.copy(rotation);
    cameraContainer.updateMatrixWorld();
  }

  private updateCameraRange(box: Mesh<BufferGeometry, MeshBasicMaterial>, index: number) {
    const { name, viewport, zoom } = this.views[index];
    const { cameraWidth, cameraHeight, cameraClip } = this.getCameraRange(box, name, zoom);
    const camera = this.views[index].camera as OrthographicCamera;
    const width = viewport[2];
    const height = viewport[3];

    let adjustWidth = cameraWidth;
    let adjustHeight = cameraHeight;

    // 将视图矩形比例重置为视野比例，取长边避免超出视野
    if (cameraWidth / cameraHeight > width / height) {
      adjustHeight = (cameraWidth * height) / width;
    } else {
      adjustWidth = (cameraHeight * width) / height;
    }

    camera.top = adjustHeight / 2;
    camera.bottom = adjustHeight / -2;
    camera.right = adjustWidth / 2;
    camera.left = adjustWidth / -2;

    camera.near = cameraClip / -2;
    camera.far = cameraClip / 2;

    camera.updateProjectionMatrix();

    return {
      type: name,
      width: cameraWidth,
      height: cameraHeight,
    };
  }

  private getCameraRange(box: Mesh<BufferGeometry, MeshBasicMaterial>, type: ThreeViewType, zoom: number) {
    const { scale } = box;
    let cameraWidth = 0,
      cameraHeight = 0,
      cameraClip = 0;
    switch (type) {
      case 'top':
        cameraWidth = scale.x * zoom;
        cameraHeight = scale.y * zoom;
        cameraClip = scale.z + 0.4;
        break;
      case 'side':
        cameraWidth = scale.x * zoom;
        cameraHeight = scale.z * zoom;
        cameraClip = scale.y + 0.4;
        break;
      case 'front':
        cameraWidth = scale.y * zoom;
        cameraHeight = scale.z * zoom;
        cameraClip = scale.x + 0.4;
        break;
      default:
        break;
    }
    return { cameraWidth, cameraHeight, cameraClip };
  }

  /**
   *
   * @param dir
   * @param type
   * @returns 方向数值：1 -> x轴正向, -1 -> x轴负向, 2 -> y轴正向, -2 -> y轴负向, 0 -> 无方向
   */
  private getBoxDirInView(dir: [number, number, number], type: ThreeViewType) {
    const { eye, target } = viewCameraOptions[type];

    // 计算立体框方向在二维视图中的坐标方向
    const [x, y] = dir.filter((_, i) => {
      return target[i] - eye[i] === 0;
    });

    if (typeof x === 'number' && typeof y === 'number') {
      // 将坐标映射为数值
      return x + y * 2;
    }
    return 0;
  }

  private createCamera(width: number, height: number, type: ThreeViewType, zoom: number) {
    const { eye, target, up, near, far } = viewCameraOptions[type];
    const aspect = width / height;
    const frustumSize = height / zoom;
    const camera = new OrthographicCamera(
      (frustumSize * aspect) / -2,
      (frustumSize * aspect) / 2,
      frustumSize / 2,
      frustumSize / -2,
      near,
      far
    );
    // 设置可视图层
    camera.layers.set(this.layer);

    camera.position.set(...eye);
    camera.up.set(...up);
    camera.lookAt(...target);
    const cameraContainer = new Group();
    cameraContainer.position.set(0, 0, 0);
    cameraContainer.rotation.set(0, 0, 0);
    cameraContainer.add(camera);
    return { cameraContainer, camera, zoom };
  }
}
